import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareSites = (s1, s2) => s1.name === s2.name

const siteSlice = createSlice({
    name: 'sites',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const s of ensureArray(action.payload)) {
                let idx = state.data.findIndex(s2 => compareSites(s, s2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, s);
                } else {
                    state.data.push(s);
                }
            }
        },
        remove: (state, action) => {
            for (const s of ensureArray(action.payload)) {
                let idx = state.data.findIndex(s2 => compareSites(s2, s));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => siteSlice.getInitialState());
    }
});

// #region network operations
const fetchSites = network => network.get('/api/v1/sites', siteSlice.actions.set);
export const createSite = (network, site) => network.post('/api/v1/site', site, siteSlice.actions.addOrUpdate);
export const updateSite = (network, site) => network.put('/api/v1/site', site, siteSlice.actions.addOrUpdate);
export const removeSite = (network, site) => network.delete('/api/v1/site/' + encodeURIComponent(site.name), siteSlice.actions.remove);
// #endregion

// #region permission checks
export const canUpdateSite = (credentials, site) => credentials.access.hasFull('administrator');
export const canCreateSite = (credentials, site) => credentials.access.hasFull('administrator');
export const canRemoveSite = (credentials, site) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectSites = state => state.sites.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectSiteByName = createSelector([selectSites, (site, name) => name], (sites, name) => sites.find(s => s.name === name));
const selectSite = name => state => selectSiteByName(state, name);
// #endregion

// #region listeners
const listen = {
    siteCreated: (site, { dispatch }) => dispatch(siteSlice.actions.addOrUpdate(site)),
    siteUpdated: (site, { dispatch }) => dispatch(siteSlice.actions.addOrUpdate(site)),
    siteRemoved: (site, { dispatch }) => dispatch(siteSlice.actions.remove(site))
}
// #endregion

export const [useSites, realizeSites] = dataHookFactory(selectSites, { run: fetchSites, listen, authenticated: false, immortal: true });
export const [useSite, realizeSite] = dataHookFactory(selectSite, name => ({ run: fetchSites, listen, authenticated: false, immortal: true }));

const reducerDetails = { name: siteSlice.name, reducer: siteSlice.reducer };
export default reducerDetails;